import styles from './index.module.css'

import cn from 'clsx'
import React, { useMemo } from 'react'

import type { TrackOptions } from '@disco/state-management/actions/MediaActions'

import { TRACK } from '@disco/constants/ItemTypes'

import type { ClientLibraryDiscoveryPanelItem } from 'types/client-library'
import type { Track } from 'types/track'

import { Flex, Grid, Text } from 'components/ds'
import Draggable from 'components/ui/draggable'
import Skeleton from 'components/ui/skeleton'

import TrackNumberDragHandle from '../../../components/track-number-drag-handle'

import AudioArtworkBtn from './audio-artwork-btn'
import { TrackMetadata } from './library-item-metadata'

type CatalogTracksGridProps = {
  tracks: Track[]
  catalog: ClientLibraryDiscoveryPanelItem
  isLoading: boolean
  businessName?: string
  error?: string | null
}

const SkeletonTrackRow = ({ index }: { index: number }) => {
  return (
    <Flex
      key={index}
      direction="column"
      gap="4"
      align="center"
      justify="center"
      width="100%"
      className={styles.TrackGrid_Container}
    >
      <Flex direction="row" gap="2" align="center" width="100%">
        <Text as="label" size="1" className={styles.SkeletonTrackNumber}>
          {index + 1}
        </Text>
        <Flex direction="row" gap="3" align="center" height="100%" width="100%">
          <Flex direction="column" gap="1" height="42px" width="42px">
            <Skeleton lines={1} type="block" />
          </Flex>
          <Flex direction="column" gap="1" height="34px" width="60%">
            <Skeleton lines={1} type="block" />
            <Skeleton lines={1} type="block" />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}

type playBackContextType = {
  catalog?: ClientLibraryDiscoveryPanelItem
} & TrackOptions

const TrackGridRow = ({
  track,
  index,
  playbackContext,
  businessName,
  clientLibraryId,
}: {
  track: Track
  index: number
  playbackContext: playBackContextType
  businessName?: string
  clientLibraryId: number
}) => {
  // Skip if track is invalid
  if (!track || !track.id) {
    return null
  }

  const title = track.name || 'Unknown Track'
  const artist = track.artist || 'Unknown Artist'

  return (
    <Draggable
      id={`discover-track-${track.id}`}
      data={{
        track,
        sourceGroupId: clientLibraryId,
        sourceGroupType: 'client_library',
        catalogName:
          playbackContext?.catalog.display_name || 'Untitled Catalog',
        catalogBusiness: businessName || 'Unknown',
        catalogId: playbackContext?.catalog.id,
      }}
      type={TRACK}
    >
      <Flex
        direction="column"
        gap="4"
        align="center"
        justify="center"
        width="100%"
        className={cn(styles.TrackGrid_Container, 'swiper-no-swiping')}
        tabIndex={0}
        role="button"
        aria-label={`Drag ${title} by ${artist} to playlist creator`}
      >
        <Flex direction="row" gap="2" align="center" width="100%">
          <TrackNumberDragHandle index={index} />
          <Flex
            direction="row"
            gap="3"
            align="center"
            height="100%"
            width="100%"
          >
            <AudioArtworkBtn
              track={track}
              playbackContext={playbackContext}
              size="42px"
              businessName={businessName}
            />
            <TrackMetadata title={title} artist={artist} />
          </Flex>
        </Flex>
      </Flex>
    </Draggable>
  )
}

export const CatalogTracksGrid: React.FC<CatalogTracksGridProps> = ({
  tracks,
  catalog,
  isLoading,
  businessName,
  error,
}) => {
  const tracksArray = useMemo(
    () => (Array.isArray(tracks) ? tracks : []),
    [tracks],
  )
  const loadingRows = isLoading ? Array.from({ length: 5 }, (_, i) => i) : []
  const clientLibraryId = catalog.id

  const playbackContext = useMemo<TrackOptions>(
    () => ({
      tracklist: tracksArray
        .filter(track => track && track.id)
        .map(track => track.id),
      playbackLocation: 'discover-music-home' as const,
      clientLibraryId,
      catalog,
      queue: true,
    }),
    [tracksArray, clientLibraryId, catalog],
  )

  if (error) {
    return (
      <Flex
        direction="column"
        maxWidth="535px"
        width="100%"
        py="4"
        align="center"
        justify="center"
      >
        <Text size="2" color="gray">
          {error}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex
      direction="column"
      maxWidth="535px"
      width="100%"
      gap="2"
      pl="3"
      align="center"
      justify="center"
    >
      <Grid className={styles.TracksGrid} width="100%" maxWidth="535px">
        {isLoading ? (
          loadingRows.map(index => (
            <SkeletonTrackRow key={index} index={index} />
          ))
        ) : (
          <>
            {tracksArray.map((track, index) => (
              <TrackGridRow
                key={track.id}
                track={track}
                index={index}
                playbackContext={playbackContext}
                businessName={businessName}
                clientLibraryId={clientLibraryId}
              />
            ))}
          </>
        )}
      </Grid>
    </Flex>
  )
}

export default CatalogTracksGrid
